<!--
  @component
  
  Version 1 of the text expander component.

  **Usage**:

  ```html
  <oc-text-expander-v1>
    Long text content goes here
  </oc-text-expander-v1>
  ```
  *Note*: Dynamically replacing the content of the text expander is not supported and can lead to unexpected behavior.

  @slot required `default` The content of the text expander
  @slot optional `show-more-text` Custom text to replace "Mehr anzeigen" on the toggle button
  @slot optional `show-less-text` Custom text to replace "Weniger anzeigen" on the toggle button
-->
<svelte:options
  customElement={{
    tag: "oc-text-expander-v1",
    props: {
      expanded: { type: "Boolean", reflect: true },
    },
  }}
/>

<script lang="ts">
  import { afterUpdate } from "svelte";

  /**
 *
 *
 *
 *
 */
  export let expanded = false;

  let contentElem: HTMLDivElement;
  let isCutOff = false;

  $: fadeout = isCutOff && !expanded;
  $: showFull = isCutOff && expanded;
  $: toggleIconType = expanded ? ("arrow-up" as const) : ("arrow-down" as const);

  afterUpdate(() => {
    if (!showFull) {
      isCutOff = contentElem.scrollHeight > contentElem.offsetHeight;
    }
  });

  function toggle() {
    expanded = !expanded;
  }

  function onFadeoutClick() {
    expanded = true;
  }
</script>

<div
  bind:this={contentElem}
  class="content"
  class:content--showFull={showFull}
  data-testid="content"
>
  <slot />
  {#if fadeout}
    <div class="fadeout" on:click={onFadeoutClick} role="presentation" data-testid="fadeout" />
  {/if}
</div>
{#if isCutOff}
  <button class="toggle" on:click={toggle} on:keyup={toggle} on:keydown={toggle}>
    <oc-icon-v1 class="toggle-icon" type={toggleIconType} size={"100"} color="currentColor" />
    {#if expanded}
      <slot name="show-less-text">Weniger anzeigen</slot>
    {:else}
      <slot name="show-more-text">Mehr anzeigen</slot>
    {/if}
  </button>
{/if}

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./TextExpanderV1.config" as config;

  /*                                                          */
  $oc-text-expander-color-background: #fff;
  $oc-text-expander-fadeout: linear-gradient(
    0deg,
    $oc-text-expander-color-background 0%,
    rgba($oc-text-expander-color-background, 0) 100%
  );
  $oc-text-expander-color-link: #008ffd;
  $oc-text-expander-color-link-hover: #0064b1;
  $oc-text-expander-font: tokens.$oc-semantic-font-copy-100;

  $toggle-height: 40px;
  $content-height: config.$oc-text-expander-max-height - $toggle-height;

  :host {
    @include config.oc-text-expander;

    background-color: $oc-text-expander-color-background;
    font: $oc-text-expander-font;
    padding-bottom: $toggle-height;
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    &[expanded] {
      @include config.oc-text-expander-expanded;
    }
  }

  .content {
    overflow: hidden;
    max-height: $content-height;

    &--showFull {
      max-height: none;
    }
  }

  .fadeout {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    background: $oc-text-expander-fadeout;
    cursor: pointer;
  }

  .toggle {
    position: absolute;
    bottom: 0;
    left: 0;
    padding-left: 40px;
    display: block;
    border: none;
    width: 100%;
    background: none;
    text-align: left;
    cursor: pointer;
    color: $oc-text-expander-color-link;
    font: $oc-text-expander-font;

    &:hover {
      color: $oc-text-expander-color-link-hover;
    }

    & .toggle-icon {
      position: absolute;
      bottom: 0;
      left: 0;
      vertical-align: middle;
    }
  }
</style>
